type SentryClient = typeof import("./sentry-client");

const SENTRY_DEDUPE_WINDOW_MS = 5000;
const MAX_DEDUPE_ENTRIES = 500;

const TARGETED_PAYOUT_MARKERS = [
  "error fetching creator payment info (by username):",
  "error fetching creator payment info (by id):",
];

const TRANSIENT_NETWORK_MARKERS = [
  "failed to fetch",
  "aborterror",
  "signal is aborted",
  "request was aborted",
  "net::err_insufficient_resources",
  "insufficient_resources",
];

const recentSentryCaptureByFingerprint = new Map<string, number>();
let sentryClientPromise: Promise<SentryClient | null> | null = null;
let sentryReadyPromise: Promise<SentryClient | null> | null = null;
let sentryInitialized = false;

const getSentryClient = (): Promise<SentryClient | null> => {
  if (!import.meta.env.VITE_SENTRY_DSN) return Promise.resolve(null);

  if (!sentryClientPromise) {
    sentryClientPromise = import("./sentry-client")
      .catch((error) => {
        console.warn("Sentry failed to load:", error);
        sentryClientPromise = null;
        return null;
      });
  }

  return sentryClientPromise;
};

const toSafeString = (value: unknown): string => {
  if (typeof value === "string") return value;
  if (value instanceof Error) return `${value.name}: ${value.message}`;

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const normalizeText = (value: unknown): string => toSafeString(value).toLowerCase();

const hasTargetedPayoutMarker = (value: unknown): boolean => {
  const text = normalizeText(value);
  return TARGETED_PAYOUT_MARKERS.some((marker) => text.includes(marker));
};

const hasTransientNetworkMarker = (value: unknown): boolean => {
  const text = normalizeText(value);
  return TRANSIENT_NETWORK_MARKERS.some((marker) => text.includes(marker));
};

const hasSuppressSentryFlag = (params: unknown[]): boolean =>
  params.some((param) => {
    return (
      !!param &&
      typeof param === "object" &&
      "suppressSentry" in param &&
      (param as { suppressSentry?: boolean }).suppressSentry === true
    );
  });

const shouldSuppressTargetedTransientPayoutError = (
  messageOrError: unknown,
  optionalParams: unknown[]
): boolean => {
  if (
    !hasTargetedPayoutMarker(messageOrError) &&
    !optionalParams.some((param) => hasTargetedPayoutMarker(param))
  ) {
    return false;
  }

  if (hasTransientNetworkMarker(messageOrError)) return true;
  return optionalParams.some((param) => hasTransientNetworkMarker(param));
};

const shouldDeduplicateSentryCapture = (
  errorToCapture: Error,
  optionalParams: unknown[]
): boolean => {
  const fingerprint = `${errorToCapture.name}:${errorToCapture.message}:${optionalParams
    .map((param) => toSafeString(param))
    .join("|")}`;
  const now = Date.now();
  const previous = recentSentryCaptureByFingerprint.get(fingerprint);
  recentSentryCaptureByFingerprint.set(fingerprint, now);

  // Keep the map bounded and remove stale entries opportunistically.
  if (recentSentryCaptureByFingerprint.size > MAX_DEDUPE_ENTRIES) {
    for (const [key, timestamp] of recentSentryCaptureByFingerprint.entries()) {
      if (now - timestamp > SENTRY_DEDUPE_WINDOW_MS) {
        recentSentryCaptureByFingerprint.delete(key);
      }
    }
  }

  return previous !== undefined && now - previous < SENTRY_DEDUPE_WINDOW_MS;
};

const getReadySentryClient = (): Promise<SentryClient | null> => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.MODE;
  const release = import.meta.env.VITE_SENTRY_RELEASE?.trim() || undefined;

  if (!dsn) return Promise.resolve(null);

  if (!sentryReadyPromise) {
    sentryReadyPromise = getSentryClient()
      .then((Sentry) => {
        if (!Sentry) {
          sentryReadyPromise = null;
          return null;
        }
        if (sentryInitialized) return Sentry;

        Sentry.init({
          dsn,
          environment,
          release,
          integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration({
              maskAllText: false,
              blockAllMedia: false,
            }),
          ],
          tracesSampleRate: environment === "production" ? 0.1 : 1.0,
          replaysSessionSampleRate: environment === "production" ? 0.1 : 1.0,
          replaysOnErrorSampleRate: 1.0,
          beforeSend(event, hint) {
            const error = hint.originalException;

            if (error && typeof error === 'object') {
              if ('message' in error) {
                const message = String(error.message);

                if (message.includes('posthog')) {
                  return null;
                }

                if (message.toLowerCase().includes('invalid login credentials')) {
                  return null;
                }
              }

              if ('name' in error && error.name === 'AuthApiError') {
                return null;
              }
            }

            if (event.contexts) {
              const contexts = Object.values(event.contexts);
              for (const context of contexts) {
                if (context && typeof context === 'object' && 'message' in context) {
                  const contextMessage = String(context.message);
                  if (contextMessage.toLowerCase().includes('invalid login credentials')) {
                    return null;
                  }
                }
              }
            }

            return event;
          },
        });
        sentryInitialized = true;
        return Sentry;
      })
      .catch((error) => {
        console.warn("Sentry failed to initialize:", error);
        sentryReadyPromise = null;
        return null;
      });
  }

  return sentryReadyPromise;
};

export function initSentry() {
  if (!import.meta.env.VITE_SENTRY_DSN) {
    console.warn("Sentry DSN not configured. Error tracking is disabled.");
    return;
  }

  void getReadySentryClient();
}

/**
 * Sets the user context in Sentry for error tracking
 * Call this when a user signs in or when user details are available
 *
 * @param user - User object with id, email, and optional additional fields
 *
 * @example
 * setSentryUser({ id: 'user123', email: 'user@example.com', userType: 'creator' });
 * setSentryUser(null); // Clear user context on sign out
 */
export function setSentryUser(user: { id: string; email?: string; [key: string]: any } | null): void {
  void getReadySentryClient().then((Sentry) => {
    if (!Sentry) return;

    if (!user) {
      Sentry.setUser(null);
      return;
    }

    Sentry.setUser({
      id: user.id,
      email: user.email,
      ...Object.fromEntries(
        Object.entries(user)
          .filter(([key]) => !['id', 'email'].includes(key))
          .map(([key, value]) => [key, value])
      )
    });
  }).catch((error) => {
    console.warn("Failed to set Sentry user:", error);
  });
}

/**
 * Logs an error to both console and Sentry
 * Use this as a drop-in replacement for console.error
 *
 * @param messageOrError - Error message, Error object, or any value to log
 * @param optionalParams - Additional parameters to log (context, metadata, etc.)
 *
 * @example
 * logError('Campaign creation failed:', error);
 * logError(new Error('Something went wrong'));
 * logError('User not found', { userId: 123 });
 */
export function logError(messageOrError: unknown, ...optionalParams: unknown[]): void {
  const isSuppressed = hasSuppressSentryFlag([messageOrError, ...optionalParams]);
  const isTargetedTransientPayoutError = shouldSuppressTargetedTransientPayoutError(
    messageOrError,
    optionalParams
  );

  if (isSuppressed || isTargetedTransientPayoutError) {
    console.warn(messageOrError, ...optionalParams);
    return;
  }

  // Always log to console for development visibility
  console.error(messageOrError, ...optionalParams);

  let errorToCapture: Error;

  if (messageOrError instanceof Error) {
    if (!messageOrError.message || messageOrError.message.trim() === '') {
      const errorStr = messageOrError.toString();
      errorToCapture = new Error(errorStr !== '[object Object]' ? errorStr : 'Unknown error occurred');
      errorToCapture.stack = messageOrError.stack;
    } else {
      errorToCapture = messageOrError;
    }
  } else {
    const message =
      typeof messageOrError === "string" ? messageOrError : toSafeString(messageOrError);
    errorToCapture = new Error(message || "Unknown error occurred");
  }

  if (shouldDeduplicateSentryCapture(errorToCapture, optionalParams)) {
    return;
  }

  void getReadySentryClient()
    .then((Sentry) => {
      if (!Sentry) return;

      if (optionalParams.length > 0) {
        Sentry.withScope((scope) => {
          optionalParams.forEach((param, index) => {
            if (param instanceof Error) {
              scope.setContext(`error_${index}`, {
                message: param.message || param.toString(),
                stack: param.stack,
                name: param.name,
              });
            } else if (typeof param === 'object' && param !== null) {
              try {
                scope.setContext(`context_${index}`, param as Record<string, unknown>);
              } catch {
                scope.setExtra(`param_${index}`, String(param));
              }
            } else {
              scope.setExtra(`param_${index}`, param);
            }
          });

          Sentry.captureException(errorToCapture);
        });
      } else {
        Sentry.captureException(errorToCapture);
      }
    })
    .catch((sentryError) => {
      console.warn('Failed to send error to Sentry:', sentryError);
    });
}

/**
 * Logs a breadcrumb (non-error) to help trace user flows like redirects and onboarding loops.
 */
export function logBreadcrumb(
  message: string,
  data?: Record<string, unknown>
): void {
  // Keep local dev visibility.
  console.log(message, data ?? {});

  void getReadySentryClient()
    .then((Sentry) => {
      if (!Sentry) return;

      Sentry.addBreadcrumb({
        message,
        category: "app",
        level: "info",
        data,
      });
    })
    .catch(() => {
      // Telemetry must never affect the product flow.
    });
}
